import { useState } from "react";
import { FileUtils } from "@live-writing/models";
import { CircleCheck, FileText, Loader2 } from "lucide-react";
import ApiClient from "../../lib/api-client";
import { useStudentStore } from "../../hooks/use-student-store";

const MaterialsSection = () => {
  const { preLesson, openedMaterials, markMaterialOpened } = useStudentStore();
  const [loadingIndex, setLoadingIndex] = useState<number | null>(null);

  if (!preLesson?.materials || preLesson.materials.length === 0) return null;

  const handleMaterialClick = async (
    e: React.MouseEvent<HTMLAnchorElement>,
    material: (typeof preLesson.materials)[number],
    index: number
  ) => {
    if (!FileUtils.isFileType(material.type) || !material.s3_key) {
      markMaterialOpened(index);
      return;
    }

    e.preventDefault();
    setLoadingIndex(index);

    try {
      const response = await ApiClient.get<{ download_url: string }>(
        `/pre-lesson/material/download?s3_key=${encodeURIComponent(material.s3_key)}`
      );
      markMaterialOpened(index);
      window.open(response.download_url, "_blank", "noopener,noreferrer");
    } catch (error) {
      console.error("Failed to get download URL:", error);
    } finally {
      setLoadingIndex(null);
    }
  };

  return (
    <div>
      <h2 className="text-lg font-semibold text-gray-900 mb-4">Lesson Materials</h2>
      <div className="space-y-3">
        {preLesson.materials.map((material, index) => (
          <a
            key={index}
            href={material.link}
            target="_blank"
            rel="noopener noreferrer"
            className={`flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-md cursor-pointer hover:bg-gray-100 transition-colors ${
              loadingIndex === index ? "pointer-events-none opacity-70" : ""
            }`}
            onClick={(e) => handleMaterialClick(e, material, index)}
          >
            <FileText className="h-10 w-10 text-blue-600 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="font-medium text-gray-900 truncate">{material.title}</p>
            </div>
            {loadingIndex === index && (
              <Loader2 className="h-4 w-4 animate-spin text-blue-600 shrink-0" />
            )}
            {openedMaterials.includes(index) && (
              <CircleCheck className="h-5 w-5 text-green-500 shrink-0" />
            )}
          </a>
        ))}
      </div>
    </div>
  );
};

export default MaterialsSection;
